import { commaListSchema, filterDateRangeSchema } from "@birdwatch/common";
import { z } from "zod";

import {
  filterSearchFragment,
  notificationSearchFragment,
  withOperators,
} from "@/features/shared/filters-new";
import { maskSearchFragment } from "@/features/shared/route-masking";

/**
 * `validateSearch` for every order list tab — `/office/orders/active`, `/all`,
 * `/future`, and `/workflow`. The tabs differ only in the data they request (see
 * `useUnifiedOrderListParams`), never in the URL keys they accept: TanStack
 * validates with the CURRENT route's schema and `z.object` strips unknown keys,
 * so a key declared on one tab but not another is silently dropped and its
 * writer just looks dead on the other tabs. One schema, one home.
 */
export const orderListSearchSchema = z.object({
  search: z.string().optional(),
  savedView: z.string().optional(),
  ...filterSearchFragment,
  filters: z
    .object({
      status: withOperators(z.string().optional()),
      lastMessageFrom: withOperators(z.string().optional()),
      lastMessageByRole: withOperators(commaListSchema),
      lastMessageAging: withOperators(z.string().optional()),
      messageUnread: withOperators(z.coerce.number().optional()),
      priority: withOperators(z.string().optional()),
      statusIds: withOperators(commaListSchema),
      categoryIds: withOperators(commaListSchema),
      purposeIds: withOperators(commaListSchema),
      flagged: withOperators(z.string().optional()),
      memberIds: withOperators(commaListSchema),
      assigneeIds: withOperators(commaListSchema),
      managerIds: withOperators(commaListSchema),
      technicianIds: withOperators(commaListSchema),
      visitStatuses: withOperators(commaListSchema),
      placeIds: withOperators(commaListSchema),
      types: withOperators(commaListSchema),
      tagIds: withOperators(commaListSchema),
      vendorIds: withOperators(commaListSchema),
      locationIds: withOperators(commaListSchema),
      createDateRange: filterDateRangeSchema,
      dateRange: filterDateRangeSchema,
      dueDateRange: filterDateRangeSchema,
      visitDateRange: filterDateRangeSchema,
      updateDateRange: filterDateRangeSchema,
      completedDateRange: filterDateRangeSchema,
      completedDateIsNull: withOperators(z.string().optional()),
      cancelledDateRange: filterDateRangeSchema,
      cancelledDateIsNull: withOperators(z.string().optional()),
      ...notificationSearchFragment,
    })
    .optional(),
  take: z.coerce.number().optional(),
  skip: z.coerce.number().optional(),
  // Kanban column-collapse state, written by `useQueryState("closeColumn")` in
  // `kanban-column-header.tsx` — which renders on every tab, so every tab
  // declares it.
  closeColumn: commaListSchema,
  // Group/lane collapse state, written by `useCollapsedKey("closeGroup")` in the
  // list group headers and the kanban swimlanes. Keys are namespaced by grouping
  // and kept short for the address bar: `status:<statusKey>`, `date:<MM-yyyy>`,
  // `assignee|manager|member|place|vendor:<id>`, and `<group>:none` for the
  // id-less lane. A kanban date lane and a list month group are the same lane in
  // two layouts, so they deliberately share the `date:` key.
  closeGroup: commaListSchema,
  sort: z.string().optional(),
  // Display options written by `useQueryState` in `use-orders-display-options`.
  // Declared here so `routeApi.useSearch()` keeps them: `useSortParam.setSort`
  // rebuilds the whole search from that snapshot, so an undeclared key is
  // dropped on every sort write. Loose strings — a `z.enum` would throw
  // SearchParamError on an unknown value; narrowing stays in the hook.
  layout: z.string().optional(),
  groupBy: z.string().optional(),
  columnBy: z.string().optional(),
  hide: z.string().optional(),
  "collapse-empty": z.string().optional(),
  // The workflow tab's picked board point — a status of the status track or a
  // point orders rest at — which narrows the list to its orders. A loose
  // string, parsed in `useUnifiedOrderListParams`; other tabs ignore it.
  point: z.string().optional(),
  ...maskSearchFragment,
});
